import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Building2, CalendarDays, Scale, Radio, Ban } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STATUS_META, fmtMoney } from "@/lib/data";
import { useAuth } from "@/lib/AuthContext";
import DocumentPanel from "@/components/DocumentPanel";
import BidPanel from "@/components/BidPanel";
import QAPanel from "@/components/QAPanel";
import AwardNotice from "@/components/AwardNotice";
import AttachmentsPanel from "@/components/AttachmentsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-[#009EDB] mt-0.5 shrink-0" aria-hidden="true" />
    <div>
      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 font-medium">{value}</p>
    </div>
  </div>
);

export default function TenderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const role = user.role;
  const [tender, setTender] = useState(null);

  const refresh = useCallback(() => {
    axios.get(`${API}/tenders/${id}`).then((r) => setTender(r.data)).catch(() => toast.error("Tender not found"));
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const publish = async () => {
    await axios.post(`${API}/tenders/${id}/publish`);
    toast.success("Tender published globally.");
    refresh();
  };
  const cancel = async () => {
    await axios.post(`${API}/tenders/${id}/cancel`);
    toast.info("Tender cancelled.");
    refresh();
  };

  if (!tender) return <p className="text-slate-400 text-sm py-20 text-center">Loading…</p>;

  const canManage = role !== "vendor";

  return (
    <div>
      {tender.status === "awarded" && <AwardNotice tenderId={tender.id} />}
      <div className="bg-white border border-slate-200 p-8 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="font-mono text-xs text-slate-400" data-testid="tender-reference">{tender.reference_number}</span>
              <span className="text-[11px] font-semibold text-[#0050A0] bg-[#E6F0F9] px-2 py-0.5">{tender.tender_type} · {tender.category}</span>
              <span className={`text-xs px-2.5 py-1 border ${STATUS_META[tender.status]?.cls}`} data-testid="tender-status-badge">
                {STATUS_META[tender.status]?.label}
              </span>
            </div>
            <h1 className="font-serif text-3xl text-slate-900 leading-tight" data-testid="tender-title">{tender.title}</h1>
            <p className="text-sm text-slate-500 mt-4 leading-relaxed max-w-3xl">{tender.description}</p>
          </div>
          {canManage && (
            <div className="flex gap-3 shrink-0">
              {tender.status === "draft" && (
                <button onClick={publish} data-testid="publish-tender-btn" className="inline-flex items-center gap-2 bg-[#0050A0] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200">
                  <Radio className="w-4 h-4" aria-hidden="true" /> Publish Globally
                </button>
              )}
              {["draft", "published"].includes(tender.status) && (
                <button onClick={cancel} data-testid="cancel-tender-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 px-5 py-2.5 text-sm font-semibold hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors duration-200">
                  <Ban className="w-4 h-4" aria-hidden="true" /> Cancel
                </button>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mt-8 pt-6 border-t border-slate-100">
          <Meta icon={Building2} label="Procuring Entity" value={`${tender.organization} — ${tender.unit_type}`} />
          <Meta icon={MapPin} label="Country of Execution" value={tender.country} />
          <Meta icon={CalendarDays} label="Deadline" value={tender.submission_deadline} />
          <Meta icon={Scale} label="Evaluation" value={tender.evaluation_method} />
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">Estimated Value</p>
            <p className="font-mono text-lg font-semibold text-slate-900" data-testid="tender-value">{fmtMoney(tender.estimated_value, tender.currency)}</p>
          </div>
        </div>
      </div>

      <AttachmentsPanel tender={tender} canManage={canManage} />

      <Tabs defaultValue="document">
        <TabsList className="bg-white border border-slate-200 rounded-none h-11 p-1">
          <TabsTrigger value="document" data-testid="tab-document" className="rounded-none data-[state=active]:bg-[#0050A0] data-[state=active]:text-white px-6">
            Tender Document
          </TabsTrigger>
          <TabsTrigger value="bids" data-testid="tab-bids" className="rounded-none data-[state=active]:bg-[#0050A0] data-[state=active]:text-white px-6">
            Bids
          </TabsTrigger>
          <TabsTrigger value="qa" data-testid="tab-qa" className="rounded-none data-[state=active]:bg-[#0050A0] data-[state=active]:text-white px-6">
            Q&A
          </TabsTrigger>
        </TabsList>
        <TabsContent value="document" className="mt-6">
          <DocumentPanel tender={tender} canManage={canManage} onGenerated={refresh} />
        </TabsContent>
        <TabsContent value="bids" className="mt-6">
          <BidPanel tender={tender} role={role} onChange={refresh} />
        </TabsContent>
        <TabsContent value="qa" className="mt-6">
          <QAPanel tender={tender} role={role} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
